import {
  PromotionType,
  ComplexPromotionTypes,
  IPackage,
} from '../../graphQL/commons'
import { getPromotionFields } from '../../utils/common/getPromotionFields'
import { Referrers } from '../../consts/defaultConfigValues/defaultConstants'
import useAppSelector from '../useAppSelector'
import {
  IAddPrefillCartItem,
  useAddPrefillCart,
} from '../prefillCart/useAddPrefillCart'
import {
  ICommonPromotionFields,
  IConditionAction,
  IProduct,
  IPromotionShortcutData,
} from '../../interfaces'

interface ITransformerItemsArgs {
  currentItems: IAddPrefillCartItem[]
  autoAdd: boolean
  promotionsFields: ICommonPromotionFields
  selectedProduct?: IProduct
  groupIdentifier?: string
}

interface IQuantityPackage {
  quantity: number
  package: IPackage | null
}

export const EmptyPackage: IPackage = {
  name: '',
  label: '',
  quantity: 0,
  price: 0,
  jumpQty: 0,
  minQty: null,
  maxQty: null,
  stock: null,
  outOfStock: null
}
/**
 * Array of conditional transformer objects for building the list of cart items
 * to add from a promotion, depending on the promotion type, selection state, and group alignment.
 * Each entry contains a `condition` to match the current context, and a `transformAction`
 * to produce an updated list of cart items.
 *
 * The array is composed of IConditionAction<ITransformerItemsArgs, IAddPrefillCartItem[]> objects.
 */
const transformersItems: IConditionAction<
  ITransformerItemsArgs,
  IAddPrefillCartItem[]
>[] = [
  /**
   * Handles COMBO promotions where a specific product is selected, and there is no package specified.
   * Adds the selected product's SKU to the cart, using the defined buy quantity, with no package info.
   */
  {
    condition: ({ promotionsFields: { type, customerBuysPackage }, selectedProduct }) =>
      type === PromotionType.COMBO && !!selectedProduct && !customerBuysPackage,
    transformAction: ({
      selectedProduct,
      promotionsFields: { customerBuysQuantity: quantity },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku: selectedProduct!.sku,
        quantity,
        package: null,
        price: selectedProduct?.price || 0,
        name: selectedProduct?.name || '',
      },
    ],
  },
  /**
   * Handles COMBO promotions where a specific product and a buy package are selected.
   * Adds the selected product's SKU and the specified package to the cart, using buy quantity.
   */
  {
    condition: ({ promotionsFields: { type, customerBuysPackage }, selectedProduct }) =>
      type === PromotionType.COMBO && !!selectedProduct && !!customerBuysPackage,
    transformAction: ({
      selectedProduct,
      promotionsFields: { customerBuysQuantity: quantity, customerBuysPackage },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku: selectedProduct!.sku,
        quantity,
        package: {
          ...EmptyPackage,
          name: customerBuysPackage!,
        },
        price: selectedProduct?.price || 0,
        name: selectedProduct?.name || '',
      },
    ],
  },
  /**
   * Handles COMBO promotions (autoAdd OFF) for "customer gets" combo options.
   * Adds the first `customerGetsItems` SKU and its defined quantity, no package, to the cart.
   */
  {
    condition: ({
      promotionsFields: { type, customerGetsItems, customerGetsQuantity, customerGetsPackage },
      autoAdd,
    }) =>
      type === PromotionType.COMBO &&
      !autoAdd &&
      !!customerGetsItems.length &&
      !!customerGetsQuantity && !customerGetsPackage,
    transformAction: ({
      promotionsFields: {
        customerGetsQuantity: quantity,
        customerGetsItems: [sku],
      },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku,
        quantity,
        package: null,
        price: 0,
        name: '',
      },
    ],
  },
  /**
   * Handles COMBO promotions (autoAdd OFF) when a get package is specified.
   * Adds the first gets SKU and its quantity, with the specified package, to the cart.
   */
  {
    condition: ({
      promotionsFields: { type, customerGetsItems, customerGetsQuantity, customerGetsPackage },
      autoAdd,
    }) =>
      type === PromotionType.COMBO &&
      !autoAdd &&
      !!customerGetsItems.length &&
      !!customerGetsQuantity &&
      !!customerGetsPackage,
    transformAction: ({
      promotionsFields: {
        customerGetsQuantity: quantity,
        customerGetsItems: [sku],
        customerGetsPackage,
      },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku,
        quantity,
        package: {
          ...EmptyPackage,
          name: customerGetsPackage!,
        },
        price: 0,
        name: '',
      },
    ],
  },
  /**
   * Handles COMBO promotions with autoAdd enabled and a percentage discount active.
   * Adds the first gets SKU and its quantity (no package) to the cart automatically.
   */
  {
    condition: ({
      promotionsFields: {
        type,
        customerGetsItems,
        customerGetsQuantity,
        customerGetsPercentage,
        customerGetsPackage,
      },
      autoAdd,
    }) =>
      type === PromotionType.COMBO &&
      !!autoAdd &&
      !!customerGetsQuantity &&
      !!customerGetsItems.length &&
      !!customerGetsPercentage &&
      !customerGetsPackage,
    transformAction: ({
      promotionsFields: {
        customerGetsItems: [sku],
        customerGetsQuantity,
      },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku,
        quantity: customerGetsQuantity,
        package: null,
        price: 0,
        name: '',
      },
    ],
  },
  /**
   * Handles COMBO promotions with autoAdd enabled, a percentage discount, and a gets package.
   * Adds the first gets SKU and its quantity, and also sets the gets package.
   */
  {
    condition: ({
      promotionsFields: {
        type,
        customerGetsItems,
        customerGetsQuantity,
        customerGetsPercentage,
        customerGetsPackage,
      },
      autoAdd,
    }) =>
      type === PromotionType.COMBO &&
      !!autoAdd &&
      !!customerGetsQuantity &&
      !!customerGetsItems.length &&
      !!customerGetsPercentage &&
      !!customerGetsPackage,
    transformAction: ({
      promotionsFields: {
        customerGetsItems: [sku],
        customerGetsQuantity,
        customerGetsPackage,
      },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku,
        quantity: customerGetsQuantity,
        package: {
          ...EmptyPackage,
          name: customerGetsPackage!,
        },
        price: 0,
        name: '',
      },
    ],
  },
  /**
   * Handles COMPLEX promotions where there is exactly one buys group and one gets group.
   * Adds all SKUs from the only buys group to the cart, ignoring missing SKUs.
   */
  {
    condition: ({
      promotionsFields: { type, customerBuysGroups, customerGetsGroups },
    }) =>
      type === PromotionType.COMPLEX &&
      customerBuysGroups.length === 1 &&
      customerGetsGroups.length === 1,
    transformAction: ({
      promotionsFields: { customerBuysGroups },
      currentItems,
    }) => {
      return [
        ...currentItems,
        // Only support one customerBuys group and one customerGets group
        ...customerBuysGroups[0].items
          .filter(({ sku }) => sku !== null)
          .map(({ sku, quantity }) => ({
            sku,
            quantity,
            package: null,
            price: 0,
            name: '',
          })),
      ]
    },
  },
  /**
   * Handles COMPLEX promotions with multiple buys groups, but only one gets group.
   * Appends all SKUs from the target buys group (matching identifier) to the cart, skipping null SKUs.
   */
  {
    condition: ({
      promotionsFields: { type, customerBuysGroups, customerGetsGroups },
    }) =>
      type === PromotionType.COMPLEX &&
      customerBuysGroups.length > 1 &&
      customerGetsGroups.length === 1,
    transformAction: ({
      promotionsFields: { customerBuysGroups },
      currentItems,
      groupIdentifier,
    }) => {
      const group = customerBuysGroups.find(
        (group) => group.identifier === groupIdentifier,
      )
      if (group) {
        return [
          ...currentItems,
          ...group.items
            .filter(({ sku }) => sku !== null)
            .map(({ sku, quantity }) => ({
              sku,
              quantity,
              package: null,
              price: 0,
              name: '',
            })),
        ]
      }
      return currentItems
    },
  },
  /**
   * Handles COMPLEX promotions (with exactly one buys group and one gets group),
   * when the gets group is either:
   * (A) Type is not DIRECT_PERCENTAGE and autoAdd is disabled, or
   * (B) Type is PERCENTAGE and autoAdd is enabled.
   * Adds all SKUs from the gets group to the cart.
   */
  {
    condition: ({
      promotionsFields: { type, customerBuysGroups, customerGetsGroups },
      autoAdd,
    }) =>
      type === PromotionType.COMPLEX &&
      customerBuysGroups.length === 1 &&
      customerGetsGroups.length === 1 &&
      ((customerGetsGroups[0].type !==
        ComplexPromotionTypes.DIRECT_PERCENTAGE &&
        !autoAdd) ||
        (customerGetsGroups[0].type === ComplexPromotionTypes.PERCENTAGE &&
          !!autoAdd)),
    transformAction: ({
      promotionsFields: { customerGetsGroups },
      currentItems,
    }) => {
      return [
        ...currentItems,
        // Only support one customerBuys group and one customerGets group
        ...customerGetsGroups[0].items
          .filter(({ sku }) => sku !== null)
          .map(
            ({ sku, quantity }) =>
              ({
                sku,
                quantity,
                package: null,
              }) as IAddPrefillCartItem,
          ),
      ]
    },
  },
  /**
   * Deduplication step. Normalizes the cart items list by merging any
   * items which share the same SKU and package name, summing quantities together.
   */
  {
    condition: ({ currentItems: { length } }) => length > 1,
    transformAction: ({ currentItems }) => {
      const separator = '+';
      const mapQuantity = currentItems.reduce<Record<string, IQuantityPackage>>(
        (acc, item) => {
          const { sku, quantity, package: packageData } = item
          const packageName = packageData?.name || ''
          const identifier = `${sku}${packageName ? `${separator}${packageName}` : ''}`
          return {
            ...acc,
            [identifier]: {
              quantity: (acc[identifier]?.quantity || 0) + quantity,
              package: packageData
            },
          }
        },
        {},
      )
      const result = Object.keys(mapQuantity).map<IAddPrefillCartItem>(
        (identifier) => {
          const [sku] = identifier.split(separator)
          const { package: packageData, quantity } = mapQuantity[identifier]
          return {
            sku,
            quantity,
            package: packageData,
            price: 0,
            name: '',
          }
        },
      )
      return result
    },
  },
]

/**
 * Custom React hook to provide logic for adding products from promotions.
 *
 * This hook is used to generate and dispatch a cart update based on a promotion (discount, bundle, etc.)
 * when a user selects a promotional action from product detail promotion button.
 *
 * It prepares the prefill cart payload according to the promotion's rules, current selections,
 * and autoAdd configuration. The payload is sent through `addPrefillCart`, so that the cart displays the correct
 * promoted items for checkout.
 *
 * @returns {{
 *   addProductFromPromotion: ({ promotion, groupIdentifier }: IPromotionShortcutData) => void
 * }} An object containing the `addProductFromPromotion` handler function.
 *
 */
export const useAddProductFromPromotion = () => {
  const selectedProduct = useAppSelector(
    (state) => state.catalogSlice.selectedProduct,
  )
  const autoAdd = useAppSelector(
    (state) =>
      state.defaultSlice.sessionData?.configuration?.promotions?.autoAdd,
  )
  const storefrontName =
    useAppSelector((state) => state.defaultSlice.storeName) || ''
  const sessionUid =
    useAppSelector((state) => state.defaultSlice.sessionId) || ''

  const { addPrefillCart } = useAddPrefillCart()

  /**
   * Handler to add products to cart from a promotion.
   *
   * @param {IPromotionShortcutData} params - The data for the promotion shortcut.
   * @param {any} params.promotion - The promotion object selected by the user.
   * @param {string} [params.groupIdentifier] - Optional identifier for a group of promotion items.
   */
  const addProductFromPromotion = ({
    promotion,
    groupIdentifier,
  }: IPromotionShortcutData) => {
    if (promotion) {
      const promotionFields = getPromotionFields(promotion)
      const transformItemsArgs: ITransformerItemsArgs = {
        currentItems: [],
        autoAdd: !!autoAdd,
        promotionsFields: promotionFields,
        selectedProduct,
      }

      const items = transformersItems.reduce<IAddPrefillCartItem[]>(
        (acc, { condition, transformAction }) =>
          condition({
            ...transformItemsArgs,
            currentItems: acc,
            groupIdentifier: groupIdentifier || '',
          })
            ? transformAction({
                ...transformItemsArgs,
                currentItems: acc,
                groupIdentifier: groupIdentifier || '',
              })
            : acc,
        [],
      )

      addPrefillCart({
        items,
        storefrontName,
        sessionUid,
        referrer: Referrers.PROMOTION_DETAIL,
        replaceCart: false,
      })
    }
  }
  return { addProductFromPromotion }
}

export default useAddProductFromPromotion
